import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Reveal, SectionHeading } from "../components/landing/Reveal";
import { Field, SectionTitle, inputCls } from "../components/landing/booking/fields";
import RabiesUpload from "../components/landing/booking/RabiesUpload";
import WaiverBlock from "../components/landing/booking/WaiverBlock";

const EMPTY = { name: "", phone: "", email: "", dogName: "", signature: "", agreed: false };

export default function WaiverPage() {
  const { settings } = useOutletContext();
  const [form, setForm] = useState(EMPTY);
  const [rabies, setRabies] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const topRef = useRef(null);
  useEffect(() => {
    if (done) topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [done]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = () => { setForm(EMPTY); setRabies(null); setDone(null); setError(""); };
  const anotherDog = () => {
    setForm((f) => ({ ...f, dogName: "", signature: "", agreed: false }));
    setRabies(null);
    setDone(null);
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/waivers", { ...form, rabiesFile: rabies?.path || "" });
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="waiver-page" className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div ref={topRef} className="scroll-mt-28" aria-hidden="true" />
        <SectionHeading
          kicker="Grooming Waiver"
          title="Sign our grooming waiver."
          description="Every pup needs a signed waiver and proof of rabies vaccination before their first spa day. It takes about two minutes, and we'll email you a copy."
        />
        <Reveal delay={0.1} className="mt-10">
          {done ? (
            <div data-testid="waiver-success" role="status" className="rounded-lg border border-grass-500/25 bg-grass-100/60 p-8 sm:p-10">
              <p className="text-2xl font-bold text-stone-900">{done.added ? `${form.dogName} added to your waiver!` : "Waiver signed. Thank you!"}</p>
              <p className="mt-2 max-w-lg text-sm text-stone-600" data-testid="waiver-success-text">
                {done.added
                  ? `${form.dogName} is now covered under your existing waiver (${done.dogCount} dogs total). We've emailed a copy to ${form.email}.`
                  : `We've saved ${form.dogName}'s signed waiver and emailed a copy to ${form.email}.`}
              </p>
              {rabies ? (
                <p data-testid="waiver-success-rabies-ok" className="mt-4 max-w-lg rounded-md border border-grass-500/30 bg-white px-4 py-3 text-sm text-grass-700">
                  {form.dogName}'s rabies vaccination record is on file. See you soon!
                </p>
              ) : (
                <p data-testid="waiver-success-rabies-reminder" className="mt-4 max-w-lg rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Reminder: please bring proof of {form.dogName}'s current rabies vaccination to the appointment. Without it we are unable to provide services.
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={anotherDog} data-testid="waiver-add-dog-button" className="btn-primary px-6 py-2.5 text-sm font-semibold">
                  Add another dog
                </button>
                <button onClick={reset} data-testid="waiver-another-button" className="btn-secondary px-6 py-2.5 text-sm font-semibold">
                  Start over
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="waiver-form" className="space-y-10 rounded-lg border border-lavender-700/10 bg-lavender-50 p-7 shadow-soft sm:p-10">
              <div data-testid="waiver-who-section">
                <SectionTitle step="01" title="Who is this waiver for?" hint="Your name and your dog's name so we can match the waiver and rabies record to the right pup. More than one dog? Sign once per dog using the same name and email." />
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <Field id="wv-name" label="Your full name *">
                    <input id="wv-name" required value={form.name} onChange={set("name")} data-testid="waiver-name-input" className={`${inputCls} mt-1.5`} placeholder="Jane Doe" autoComplete="name" />
                  </Field>
                  <Field id="wv-dog" label="Dog's name *">
                    <input id="wv-dog" required value={form.dogName} onChange={set("dogName")} data-testid="waiver-dog-input" className={`${inputCls} mt-1.5`} placeholder="Bella" />
                  </Field>
                  <Field id="wv-phone" label="Phone *">
                    <input id="wv-phone" type="tel" required value={form.phone} onChange={set("phone")} data-testid="waiver-phone-input" className={`${inputCls} mt-1.5`} placeholder="860-555-0123" autoComplete="tel" />
                  </Field>
                  <Field id="wv-email" label="Email * (we'll send your copy here)">
                    <input id="wv-email" type="email" required value={form.email} onChange={set("email")} data-testid="waiver-email-input" className={`${inputCls} mt-1.5`} placeholder="jane@email.com" autoComplete="email" />
                  </Field>
                </div>
              </div>

              <div data-testid="waiver-rabies-section">
                <SectionTitle step="02" title="Rabies vaccination" hint="Upload it now or bring it with you." />
                <div className="mt-5">
                  <RabiesUpload value={rabies} onChange={setRabies} />
                </div>
              </div>

              <div data-testid="waiver-sign-section">
                <SectionTitle step="03" title="Read and sign" hint="Scroll through the agreement, then sign below." />
                <div className="mt-5">
                  <WaiverBlock
                    idPrefix="wv"
                    text={settings.waiverText}
                    agreed={form.agreed}
                    signature={form.signature}
                    onAgree={(v) => setForm((f) => ({ ...f, agreed: v }))}
                    onSign={(v) => setForm((f) => ({ ...f, signature: v }))}
                  />
                </div>
              </div>

              {error && <p data-testid="waiver-error" className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-stone-500">* Required. A copy of the signed waiver is emailed to you right away.</p>
                <button type="submit" disabled={busy} data-testid="waiver-submit-button" className="btn-primary inline-flex items-center px-8 py-3.5 text-sm font-semibold disabled:opacity-60">
                  {busy ? "Signing…" : "Sign waiver"}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
